import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import { Car, Lock, Eye, EyeOff, ArrowLeft, CheckCircle, XCircle } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Toaster } from '../../components/ui/sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ResetPasswordPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [verifying, setVerifying] = useState(true);
  const [tokenValid, setTokenValid] = useState(false);
  const [maskedEmail, setMaskedEmail] = useState('');
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    const verifyToken = async () => {
      if (!token) {
        setVerifying(false);
        setTokenValid(false);
        return;
      }

      try {
        const response = await axios.get(`${API_URL}/api/admin/verify-reset-token/${token}`);
        setTokenValid(true);
        setMaskedEmail(response.data.email);
      } catch (error) {
        setTokenValid(false);
        console.error('Token verification failed:', error);
      } finally {
        setVerifying(false);
      }
    };

    verifyToken();
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!password || !confirmPassword) {
      toast.error('Veuillez remplir tous les champs');
      return;
    }

    if (password.length < 6) {
      toast.error('Le mot de passe doit contenir au moins 6 caractères');
      return;
    }

    if (password !== confirmPassword) {
      toast.error('Les mots de passe ne correspondent pas');
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API_URL}/api/admin/reset-password`, {
        token,
        new_password: password
      });
      setSuccess(true);
      toast.success('Mot de passe réinitialisé avec succès !');
    } catch (error) {
      console.error('Error:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors de la réinitialisation');
    } finally {
      setLoading(false);
    }
  };

  // Loading state
  if (verifying) {
    return (
      <div className="min-h-screen bg-[#F9FAFB] flex items-center justify-center p-4">
        <div className="text-center">
          <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-500">Vérification du lien...</p>
        </div>
      </div>
    );
  }

  // Invalid or expired token
  if (!tokenValid) {
    return (
      <div className="min-h-screen bg-[#F9FAFB] flex items-center justify-center p-4">
        <Toaster position="top-right" richColors />
        
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="w-full max-w-md"
        >
          <div className="glass-surface p-8 space-y-6 text-center">
            <div className="w-16 h-16 bg-red-100 mx-auto flex items-center justify-center mb-4 rounded-full">
              <XCircle className="w-8 h-8 text-red-500" strokeWidth={1.5} />
            </div>
            
            <h1 className="font-serif text-2xl text-[#181B1F] font-light">
              Lien invalide ou expiré
            </h1>
            
            <p className="text-gray-500">
              Ce lien de réinitialisation n'est plus valide. Les liens expirent après 1 heure pour des raisons de sécurité.
            </p>
            
            <div className="pt-4 space-y-3">
              <Button
                onClick={() => navigate('/admin/forgot-password')}
                className="w-full btn-primary"
              >
                Demander un nouveau lien
              </Button>
              
              <Button
                onClick={() => navigate('/admin')}
                variant="outline"
                className="w-full"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Retour à la connexion
              </Button>
            </div>
          </div>
        </motion.div>
      </div>
    );
  }

  // Success state
  if (success) {
    return (
      <div className="min-h-screen bg-[#F9FAFB] flex items-center justify-center p-4">
        <Toaster position="top-right" richColors />
        
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="w-full max-w-md"
        >
          <div className="glass-surface p-8 space-y-6 text-center">
            <div className="w-16 h-16 bg-green-100 mx-auto flex items-center justify-center mb-4 rounded-full">
              <CheckCircle className="w-8 h-8 text-green-500" strokeWidth={1.5} />
            </div>
            
            <h1 className="font-serif text-2xl text-[#181B1F] font-light">
              Mot de passe réinitialisé !
            </h1>
            
            <p className="text-gray-500">
              Votre mot de passe a été modifié avec succès. Vous pouvez maintenant vous connecter avec votre nouveau mot de passe.
            </p>
            
            <div className="pt-4">
              <Button
                onClick={() => navigate('/admin')}
                className="w-full btn-primary"
              >
                Se connecter
              </Button>
            </div>
          </div>
        </motion.div>
      </div>
    );
  }

  // Reset form
  return (
    <div className="min-h-screen bg-[#F9FAFB] flex items-center justify-center p-4">
      <Toaster position="top-right" richColors />
      
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md"
      >
        <div className="glass-surface p-8 space-y-8">
          {/* Logo */}
          <div className="text-center">
            <div className="w-16 h-16 bg-primary mx-auto flex items-center justify-center mb-4">
              <Car className="w-8 h-8 text-[#181B1F]" strokeWidth={1.5} />
            </div>
            <h1 className="font-serif text-2xl text-[#181B1F] font-light">
              Nouveau mot de passe
            </h1>
            <p className="text-gray-500 text-sm mt-2">
              Compte : {maskedEmail}
            </p>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="password" className="text-gray-500">Nouveau mot de passe</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="input-default pl-10 pr-10"
                  placeholder="Min. 6 caractères"
                  data-testid="reset-password-new"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="confirmPassword" className="text-gray-500">Confirmer le mot de passe</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  id="confirmPassword"
                  type={showPassword ? 'text' : 'password'}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="input-default pl-10"
                  placeholder="Confirmer le mot de passe"
                  data-testid="reset-password-confirm"
                />
              </div>
            </div>

            <Button 
              type="submit" 
              disabled={loading}
              className="w-full btn-primary"
              data-testid="reset-password-submit"
            >
              {loading ? 'Réinitialisation...' : 'Réinitialiser le mot de passe'}
            </Button>
          </form>
        </div>

        {/* Back to login */}
        <div className="text-center mt-6">
          <Link
            to="/admin"
            className="text-gray-400 hover:text-[#181B1F] text-sm transition-colors inline-flex items-center"
          >
            <ArrowLeft className="w-4 h-4 mr-1" />
            Retour à la connexion
          </Link>
        </div>
      </motion.div>
    </div>
  );
};

export default ResetPasswordPage;
